/**
 * Permission Metadata Configuration
 *
 * This file provides human-readable descriptions and categorization for all system permissions.
 * Used by the Security.vue Role Details dialog to display meaningful permission names.
 *
 * Reference: GitHub Issue #1127 - RBAC Implementation Plan Phase 4
 */

export interface PermissionMetadata {
	/** The permission key that matches SystemPermissions enum */
	key: string;
	/** The module/area this permission belongs to */
	module: string;
	/** The resource being accessed */
	resource: string;
	/** The action being performed */
	action: string;
	/** Human-readable description of what this permission allows */
	description: string;
}

export interface PermissionModule {
	/** Display name for the module */
	name: string;
	/** Icon for the module (mdi icon name) */
	icon: string;
	/** Sort order for display */
	order: number;
}

/**
 * Module definitions for grouping permissions
 */
export const permissionModules: Record<string, PermissionModule> = {
	SalesOrders: {
		name: "Sales Orders",
		icon: "mdi-cart",
		order: 1,
	},
	Purchasing: {
		name: "Purchasing",
		icon: "mdi-truck-delivery",
		order: 2,
	},
	Receiving: {
		name: "Receiving",
		icon: "mdi-package-variant",
		order: 3,
	},
	Inventory: {
		name: "Inventory",
		icon: "mdi-warehouse",
		order: 4,
	},
	Relationships: {
		name: "Relationships",
		icon: "mdi-account-group",
		order: 5,
	},
	Settings: {
		name: "Settings",
		icon: "mdi-cog",
		order: 6,
	},
	Reports: {
		name: "Reports",
		icon: "mdi-chart-bar",
		order: 7,
	},
	Shipping: {
		name: "Shipping",
		icon: "mdi-truck-fast",
		order: 8,
	},
	Shipper: {
		name: "Shipper",
		icon: "mdi-package-variant-closed",
		order: 9,
	},
};

/**
 * Complete permission metadata for all system permissions
 */
export const permissionMetadata: PermissionMetadata[] = [
	// ===== SALES ORDERS MODULE =====
	// Quotes
	{
		key: "SalesOrders_Quote_Search",
		module: "SalesOrders",
		resource: "Quote",
		action: "Search",
		description: "Search and filter quotes",
	},
	{
		key: "SalesOrders_Quote_View",
		module: "SalesOrders",
		resource: "Quote",
		action: "View",
		description: "View quote details",
	},
	{
		key: "SalesOrders_Quote_Create",
		module: "SalesOrders",
		resource: "Quote",
		action: "Create",
		description: "Create new quotes",
	},
	{
		key: "SalesOrders_Quote_Update",
		module: "SalesOrders",
		resource: "Quote",
		action: "Update",
		description: "Modify existing quotes",
	},
	{
		key: "SalesOrders_Quote_Cancel",
		module: "SalesOrders",
		resource: "Quote",
		action: "Cancel",
		description: "Cancel quotes",
	},
	{
		key: "SalesOrders_Quote_PlaceOrder",
		module: "SalesOrders",
		resource: "Quote",
		action: "Place Order",
		description: "Convert quote to order",
	},
	{
		key: "SalesOrders_Quote_Notes_ALL",
		module: "SalesOrders",
		resource: "Quote Notes",
		action: "All",
		description: "Manage quote notes",
	},
	{
		key: "SalesOrders_Quote_Payments_ALL",
		module: "SalesOrders",
		resource: "Quote Payments",
		action: "All",
		description: "Manage quote payments",
	},
	// Quote Approval
	{
		key: "SalesOrders_PendingApproval_View",
		module: "SalesOrders",
		resource: "Pending Approval",
		action: "View",
		description: "View pending approvals list",
	},
	{
		key: "SalesOrders_Quote_Approve",
		module: "SalesOrders",
		resource: "Quote",
		action: "Approve",
		description: "Approve pending quotes",
	},
	{
		key: "SalesOrders_Quote_Revert",
		module: "SalesOrders",
		resource: "Quote",
		action: "Revert",
		description: "Revert approved quotes to pending",
	},
	{
		key: "SalesOrders_Quote_AdjustTaxRate",
		module: "SalesOrders",
		resource: "Quote",
		action: "Adjust Tax",
		description: "Override tax rate on quotes",
	},
	// Orders
	{
		key: "SalesOrders_Order_Search",
		module: "SalesOrders",
		resource: "Order",
		action: "Search",
		description: "Search and filter orders",
	},
	{
		key: "SalesOrders_Order_View",
		module: "SalesOrders",
		resource: "Order",
		action: "View",
		description: "View order details",
	},
	{
		key: "SalesOrders_Order_Update",
		module: "SalesOrders",
		resource: "Order",
		action: "Update",
		description: "Modify existing orders",
	},
	{
		key: "SalesOrders_Order_OverrideShippingAndHandling",
		module: "SalesOrders",
		resource: "Order",
		action: "Override S&H",
		description: "Change shipping and handling on an order after it has been processed",
	},
	{
		key: "SalesOrders_Order_Cancel",
		module: "SalesOrders",
		resource: "Order",
		action: "Cancel",
		description: "Cancel orders",
	},
	{
		key: "SalesOrders_Order_Process",
		module: "SalesOrders",
		resource: "Order",
		action: "Process",
		description: "Process approved orders",
	},
	{
		key: "SalesOrders_Order_Notes_ALL",
		module: "SalesOrders",
		resource: "Order Notes",
		action: "All",
		description: "Manage order notes",
	},
	{
		key: "SalesOrders_Order_Payments_ALL",
		module: "SalesOrders",
		resource: "Order Payments",
		action: "All",
		description: "Manage order payments",
	},
	// Order Status
	{
		key: "SalesOrders_OrderStatus_PrintPickTicket",
		module: "SalesOrders",
		resource: "Order Status",
		action: "Print Pick Ticket",
		description: "Print pick tickets from order status",
	},
	{
		key: "SalesOrders_OrderStatus_FillOrders",
		module: "SalesOrders",
		resource: "Order Status",
		action: "Fill Orders",
		description: "Fill orders from available inventory",
	},
	{
		key: "SalesOrders_OrderStatus_PrintInvoice",
		module: "SalesOrders",
		resource: "Order Status",
		action: "Print Invoice",
		description: "Print invoices",
	},
	{
		key: "SalesOrders_OrderStatus_PrintOrder",
		module: "SalesOrders",
		resource: "Order Status",
		action: "Print Order",
		description: "Print order documents",
	},
	{
		key: "SalesOrders_OrderStatus_PrintVendorPOS",
		module: "SalesOrders",
		resource: "Order Status",
		action: "Print Vendor POs",
		description: "Print vendor purchase orders",
	},
	{
		key: "SalesOrders_OrderStatus_PrintPackingSlips",
		module: "SalesOrders",
		resource: "Order Status",
		action: "Print Packing Slips",
		description: "Print packing slips",
	},
	// Find Related
	{
		key: "SalesOrders_FindRelatedOrders_Search",
		module: "SalesOrders",
		resource: "Find Related",
		action: "Search",
		description: "Search for related orders",
	},
	// Sample Orders
	{
		key: "SalesOrders_SampleOrder_View",
		module: "SalesOrders",
		resource: "Sample Orders",
		action: "View",
		description: "View sample orders",
	},
	{
		key: "SalesOrders_SampleOrder_MarkReturned",
		module: "SalesOrders",
		resource: "Sample Orders",
		action: "Mark Returned",
		description: "Mark samples as returned",
	},

	// ===== PURCHASING MODULE =====
	{
		key: "Purchasing_PurchaseOrders_Search",
		module: "Purchasing",
		resource: "Purchase Orders",
		action: "Search",
		description: "Search purchase orders",
	},
	{
		key: "Purchasing_PurchaseOrders_View",
		module: "Purchasing",
		resource: "Purchase Orders",
		action: "View",
		description: "View purchase order details",
	},
	{
		key: "Purchasing_PurchaseOrders_Create",
		module: "Purchasing",
		resource: "Purchase Orders",
		action: "Create",
		description: "Create new purchase orders",
	},
	{
		key: "Purchasing_PurchaseOrders_PlacePO",
		module: "Purchasing",
		resource: "Purchase Orders",
		action: "Place PO",
		description: "Submit purchase orders to vendors",
	},
	{
		key: "Purchasing_PurchaseOrders_Update_Unlock",
		module: "Purchasing",
		resource: "Purchase Orders",
		action: "Update/Unlock",
		description: "Modify or unlock purchase orders",
	},
	{
		key: "Purchasing_PurchaseOrders_CancelPO",
		module: "Purchasing",
		resource: "Purchase Orders",
		action: "Cancel",
		description: "Cancel purchase orders",
	},
	{
		key: "Purchasing_PurchaseOrder_Notes_ALL",
		module: "Purchasing",
		resource: "PO Notes",
		action: "All",
		description: "Manage purchase order notes",
	},
	{
		key: "Purchasing_PurchaseOrders_Print",
		module: "Purchasing",
		resource: "Purchase Orders",
		action: "Print",
		description: "Print purchase orders",
	},
	{
		key: "Purchasing_POsToGenerate_Search",
		module: "Purchasing",
		resource: "POs to Generate",
		action: "Search",
		description: "View POs that need to be generated",
	},
	{
		key: "Purchasing_POsToGenerate_PlaceStockPO",
		module: "Purchasing",
		resource: "POs to Generate",
		action: "Place Stock PO",
		description: "Generate stock purchase orders",
	},
	{
		key: "Purchasing_Supplier_Search_View",
		module: "Purchasing",
		resource: "Supplier",
		action: "Search/View",
		description: "View supplier information",
	},
	{
		key: "Purchasing_Supplier_Update",
		module: "Purchasing",
		resource: "Supplier",
		action: "Update",
		description: "Modify supplier information",
	},
	{
		key: "Purchasing_Supplier_Delete",
		module: "Purchasing",
		resource: "Supplier",
		action: "Delete",
		description: "Remove suppliers",
	},

	// ===== RECEIVING MODULE =====
	{
		key: "Receiving_ReceivingEntry_Search_View",
		module: "Receiving",
		resource: "Receiving Entry",
		action: "Search/View",
		description: "Search receiving entries",
	},
	{
		key: "Receiving_ReceivingEntry_View",
		module: "Receiving",
		resource: "Receiving Entry",
		action: "View",
		description: "View receiving details",
	},
	{
		key: "Receiving_ReceivingEntry_Receive_UnReceive",
		module: "Receiving",
		resource: "Receiving Entry",
		action: "Receive/Un-Receive",
		description: "Receive or unreceive inventory",
	},
	{
		key: "Receiving_Commits_Search_View",
		module: "Receiving",
		resource: "Commits",
		action: "Search/View",
		description: "View commit queue",
	},
	{
		key: "Receiving_Commits_Commit_Decommit",
		module: "Receiving",
		resource: "Commits",
		action: "Commit/De-commit",
		description: "Commit or de-commit inventory to orders",
	},
	{
		key: "Receiving_Commits_PrintPickTicket",
		module: "Receiving",
		resource: "Commits",
		action: "Print Pick Ticket",
		description: "Print pick tickets from commits",
	},

	// ===== INVENTORY MODULE =====
	{
		key: "Inventory_AvailableInventory_Search_View",
		module: "Inventory",
		resource: "Available Inventory",
		action: "Search/View",
		description: "View available inventory levels",
	},
	{
		key: "Inventory_InventoryItems_View_Search",
		module: "Inventory",
		resource: "Inventory Items",
		action: "View/Search",
		description: "Browse and search inventory items",
	},
	{
		key: "Inventory_InventoryItems_Create_Update_Delete",
		module: "Inventory",
		resource: "Inventory Items",
		action: "Create/Update/Delete",
		description: "Manage inventory items",
	},
	{
		key: "Inventory_BinUtility_View_Search",
		module: "Inventory",
		resource: "Bin Utility",
		action: "View/Search",
		description: "View bin locations",
	},
	{
		key: "Inventory_BinUtility_Update",
		module: "Inventory",
		resource: "Bin Utility",
		action: "Update",
		description: "Modify bin assignments",
	},
	{
		key: "Inventory_SizeChart_View_Search",
		module: "Inventory",
		resource: "Size Chart",
		action: "View/Search",
		description: "View size charts",
	},
	{
		key: "Inventory_SizeChart_Create_Update_Delete",
		module: "Inventory",
		resource: "Size Chart",
		action: "Create/Update/Delete",
		description: "Manage size charts",
	},
	{
		key: "Inventory_ColorPalette_View_Search",
		module: "Inventory",
		resource: "Color Palette",
		action: "View/Search",
		description: "View color palettes",
	},
	{
		key: "Inventory_ColorPalette_Create_Update_Delete",
		module: "Inventory",
		resource: "Color Palette",
		action: "Create/Update/Delete",
		description: "Manage color palettes",
	},
	{
		key: "Inventory_PackageCategory_View_Search",
		module: "Inventory",
		resource: "Package Category",
		action: "View/Search",
		description: "View package categories",
	},
	{
		key: "Inventory_PackageCategory_Create_Update_Delete",
		module: "Inventory",
		resource: "Package Category",
		action: "Create/Update/Delete",
		description: "Manage package categories",
	},
	{
		key: "Inventory_SubItemCategory_View_Search",
		module: "Inventory",
		resource: "Sub-Item Category",
		action: "View/Search",
		description: "View sub-item categories",
	},
	{
		key: "Inventory_SubItemCategory_Create_Update_Delete",
		module: "Inventory",
		resource: "Sub-Item Category",
		action: "Create/Update/Delete",
		description: "Manage sub-item categories",
	},
	{
		key: "Inventory_PositionOfLetters_View",
		module: "Inventory",
		resource: "Position of Letters",
		action: "View",
		description: "View letter positions",
	},
	{
		key: "Inventory_PositionOfLetters_Create_Update_Delete",
		module: "Inventory",
		resource: "Position of Letters",
		action: "Create/Update/Delete",
		description: "Manage letter positions",
	},
	{
		key: "Inventory_ProcessTypes_View",
		module: "Inventory",
		resource: "Process Types",
		action: "View",
		description: "View process types",
	},
	{
		key: "Inventory_ProcessTypes_Create_Update_Delete",
		module: "Inventory",
		resource: "Process Types",
		action: "Create/Update/Delete",
		description: "Manage process types",
	},

	// ===== RELATIONSHIPS MODULE =====
	{
		key: "Relationships_Customer_ALL",
		module: "Relationships",
		resource: "Customer",
		action: "All",
		description: "Full customer management access",
	},
	{
		key: "Relationships_Vendor_ALL",
		module: "Relationships",
		resource: "Vendor",
		action: "All",
		description: "Full vendor management access",
	},
	{
		key: "Relationships_Rep_ALL",
		module: "Relationships",
		resource: "Rep",
		action: "All",
		description: "Full sales rep management access",
	},

	// ===== SETTINGS MODULE =====
	{
		key: "Settings_CustomerCategory_ALL",
		module: "Settings",
		resource: "Customer Category",
		action: "All",
		description: "Manage customer categories",
	},
	{
		key: "Settings_PaymentMethod_ALL",
		module: "Settings",
		resource: "Payment Method",
		action: "All",
		description: "Manage payment methods",
	},
	{
		key: "Settings_PaymentTerms_ALL",
		module: "Settings",
		resource: "Payment Terms",
		action: "All",
		description: "Manage payment terms",
	},
	{
		key: "Settings_ShippingMethod_ALL",
		module: "Settings",
		resource: "Shipping Method",
		action: "All",
		description: "Manage shipping methods",
	},
	{
		key: "Settings_Messages_ALL",
		module: "Settings",
		resource: "Messages",
		action: "All",
		description: "Manage system messages",
	},
	{
		key: "Settings_Security_ALL",
		module: "Settings",
		resource: "Security",
		action: "All",
		description: "Manage users and roles",
	},
	{
		key: "Settings_PrintingLog_ALL",
		module: "Settings",
		resource: "Printing Log",
		action: "All",
		description: "View print history",
	},
	{
		key: "Settings_Printers_ALL",
		module: "Settings",
		resource: "Printers",
		action: "All",
		description: "Manage printers",
	},
	{
		key: "Settings_PackageTypes_ALL",
		module: "Settings",
		resource: "Package Types",
		action: "All",
		description: "Manage shipping package types",
	},
	{
		key: "Settings_ZipTaxRate_ALL",
		module: "Settings",
		resource: "Zip Tax Rate",
		action: "All",
		description: "Manage tax rates by zip code",
	},
	{
		key: "Settings_SystemCategory_ALL",
		module: "Settings",
		resource: "System Category",
		action: "All",
		description: "Manage system categories",
	},

	// ===== REPORTS MODULE =====
	{
		key: "Reports_RepActivitySummary_ALL",
		module: "Reports",
		resource: "Rep Activity Summary",
		action: "All",
		description: "View rep activity summary reports",
	},
	{
		key: "Reports_SalesSummaryByCustomerOrder_ALL",
		module: "Reports",
		resource: "Sales Summary by Customer Order",
		action: "All",
		description: "View sales summary by customer order",
	},
	{
		key: "Reports_SalesDailySummaryByRep_ALL",
		module: "Reports",
		resource: "Sales Daily Summary by Rep",
		action: "All",
		description: "View daily sales summary by rep",
	},
	{
		key: "Reports_SalesByStyleCode_ALL",
		module: "Reports",
		resource: "Sales by Style Code",
		action: "All",
		description: "View sales by style code",
	},
	{
		key: "Reports_CustomerOpenOrdersLineItems_ALL",
		module: "Reports",
		resource: "Customer Open Orders Line Items",
		action: "All",
		description: "View customer open order line items",
	},
	{
		key: "Reports_QuoteStatus_ALL",
		module: "Reports",
		resource: "Quote Status",
		action: "All",
		description: "View quote status reports",
	},
	{
		key: "Reports_SamplesOverdue_ALL",
		module: "Reports",
		resource: "Samples Overdue",
		action: "All",
		description: "View overdue samples reports",
	},
	{
		key: "Reports_OutstandingBalanceByRep_ALL",
		module: "Reports",
		resource: "Outstanding Balance by Rep",
		action: "All",
		description: "View outstanding balance by rep",
	},
	{
		key: "Reports_SalesTaxReport_ALL",
		module: "Reports",
		resource: "Sales Tax Report",
		action: "All",
		description: "View sales tax reports",
	},
	{
		key: "Reports_SalesTaxByZipReport_ALL",
		module: "Reports",
		resource: "Sales Tax by Zip Report",
		action: "All",
		description: "View sales tax by zip code",
	},
	{
		key: "Reports_CustomerOrdersReport_ALL",
		module: "Reports",
		resource: "Customer Orders Report",
		action: "All",
		description: "View customer orders reports",
	},
	{
		key: "Reports_QuoteNeedsReport_ALL",
		module: "Reports",
		resource: "Quote Needs Report",
		action: "All",
		description: "View quote needs reports",
	},
	{
		key: "Reports_RepActivityComparison_ALL",
		module: "Reports",
		resource: "Rep Activity Comparison",
		action: "All",
		description: "Compare rep activity",
	},
	{
		key: "Reports_OpenPOReport_ALL",
		module: "Reports",
		resource: "Open PO Report",
		action: "All",
		description: "View open purchase order reports",
	},
	{
		key: "Reports_QuoteOrderCrossRef_ALL",
		module: "Reports",
		resource: "Quote/Order Cross Ref",
		action: "All",
		description: "View quote/order cross reference",
	},
	{
		key: "Reports_GenerateMailingList_ALL",
		module: "Reports",
		resource: "Generate Mailing List",
		action: "All",
		description: "Generate mailing lists",
	},
	{
		key: "Reports_SalesSummaryByState_ALL",
		module: "Reports",
		resource: "Sales Summary by State",
		action: "All",
		description: "View sales summary by state",
	},
	{
		key: "Reports_SalesDetailByOrderRep_ALL",
		module: "Reports",
		resource: "Sales Detail by Order & Rep",
		action: "All",
		description: "View sales detail by order and rep",
	},
	{
		key: "Reports_SalesDetailByStyleCodeRep_ALL",
		module: "Reports",
		resource: "Sales Detail by Style Code & Rep",
		action: "All",
		description: "View sales detail by style code and rep",
	},
	{
		key: "Reports_ItemOrderActivitySummaryByMonth_ALL",
		module: "Reports",
		resource: "Item Order Activity Summary",
		action: "All",
		description: "View item order activity by month",
	},
	{
		key: "Reports_OutstandingCreditByRep_ALL",
		module: "Reports",
		resource: "Outstanding Credit by Rep",
		action: "All",
		description: "View outstanding credit by rep",
	},
	{
		key: "Reports_PriceDeviationReport_ALL",
		module: "Reports",
		resource: "Price Deviation Report",
		action: "All",
		description: "View price deviation reports",
	},
	{
		key: "Reports_CustomerSales_3Year_ALL",
		module: "Reports",
		resource: "Customer Sales - 3 Year",
		action: "All",
		description: "View 3-year customer sales history",
	},
	{
		key: "Reports_SalesByMonth_ALL",
		module: "Reports",
		resource: "Sales by Month",
		action: "All",
		description: "View sales by month",
	},
	{
		key: "Reports_ShipReportForReps_ALL",
		module: "Reports",
		resource: "Ship Report for Reps",
		action: "All",
		description: "View shipping reports for reps",
	},
	{
		key: "Reports_SalesCVSTopStates_ALL",
		module: "Reports",
		resource: "Sales CVS Top States",
		action: "All",
		description: "View CVS top states sales",
	},
	{
		key: "Reports_POReportByVendor_ALL",
		module: "Reports",
		resource: "PO Report by Vendor",
		action: "All",
		description: "View purchase orders by vendor",
	},
	{
		key: "Reports_AvailableStock_ALL",
		module: "Reports",
		resource: "Available Stock",
		action: "All",
		description: "View available stock reports",
	},
	{
		key: "Reports_DemandHistory_ALL",
		module: "Reports",
		resource: "Demand History",
		action: "All",
		description: "View demand history reports",
	},
	{
		key: "Reports_Vendor_ALL",
		module: "Reports",
		resource: "Vendor",
		action: "All",
		description: "View vendor reports",
	},
	{
		key: "Reports_SuppliedBy_ALL",
		module: "Reports",
		resource: "Supplied By",
		action: "All",
		description: "View supplied by reports",
	},

	// ===== SHIPPING MODULE (API-level) =====
	{
		key: "Shipping_Shipments_Create",
		module: "Shipping",
		resource: "Shipments",
		action: "Create",
		description: "Create shipments via API",
	},
	{
		key: "Shipping_Shipments_View",
		module: "Shipping",
		resource: "Shipments",
		action: "View",
		description: "View shipments via API",
	},
	{
		key: "Shipping_Labels_Create",
		module: "Shipping",
		resource: "Labels",
		action: "Create",
		description: "Create shipping labels via API",
	},
	{
		key: "Shipping_Rates_View",
		module: "Shipping",
		resource: "Rates",
		action: "View",
		description: "View shipping rates via API",
	},
	{
		key: "Shipping_Carriers_View",
		module: "Shipping",
		resource: "Carriers",
		action: "View",
		description: "View carriers via API",
	},

	// ===== SHIPPER MODULE =====
	{
		key: "Shipper_Shipping_CreateLabel",
		module: "Shipper",
		resource: "Shipping",
		action: "Create Label",
		description: "Create shipping labels",
	},
	{
		key: "Shipper_Shipping_ViewPickTicket",
		module: "Shipper",
		resource: "Pick Ticket",
		action: "View",
		description: "View pick tickets for shipping",
	},
	{
		key: "Shipper_Shipping_VoidLabel",
		module: "Shipper",
		resource: "Shipping",
		action: "Void Label",
		description: "Void shipping labels",
	},
	{
		key: "Shipper_Shipping_CreateReturnLabel",
		module: "Shipper",
		resource: "Shipping",
		action: "Create Return",
		description: "Create return shipping labels",
	},
	{
		key: "Shipper_Shipping_ViewLabels",
		module: "Shipper",
		resource: "Labels",
		action: "View",
		description: "View shipping labels",
	},
	{
		key: "Shipper_PickTicket_Update",
		module: "Shipper",
		resource: "Pick Ticket",
		action: "Update",
		description: "Update pick ticket details",
	},
	{
		key: "Shipper_PickTicket_Unship",
		module: "Shipper",
		resource: "Pick Ticket",
		action: "Unship",
		description: "Unship pick tickets",
	},
	{
		key: "Shipper_RecentActivity_View",
		module: "Shipper",
		resource: "Recent Activity",
		action: "View",
		description: "View recent shipping activity",
	},
	{
		key: "Shipper_EndOfDay_View",
		module: "Shipper",
		resource: "End of Day",
		action: "View",
		description: "View end of day summary",
	},
	{
		key: "Shipper_EndOfDay_Export",
		module: "Shipper",
		resource: "End of Day",
		action: "Export",
		description: "Export end of day data",
	},
	{
		key: "Shipper_Manifest_Create",
		module: "Shipper",
		resource: "Manifest",
		action: "Create",
		description: "Create carrier manifests",
	},
	{
		key: "Shipper_Manifest_View",
		module: "Shipper",
		resource: "Manifest",
		action: "View",
		description: "View carrier manifests",
	},

	// ===== SYSTEM =====
	{
		key: "RegisterUser",
		module: "Settings",
		resource: "User",
		action: "Register",
		description: "Register new users",
	},
];

/**
 * Get permission metadata by key
 */
export function getPermissionMetadata(key: string): PermissionMetadata | undefined {
	return permissionMetadata.find((p) => p.key === key);
}

/**
 * Get all permissions for a module
 */
export function getPermissionsByModule(moduleName: string): PermissionMetadata[] {
	return permissionMetadata.filter((p) => p.module === moduleName);
}

/**
 * Get permissions grouped by module
 */
export function getPermissionsGroupedByModule(): Record<string, PermissionMetadata[]> {
	return permissionMetadata.reduce(
		(acc, permission) => {
			if (!acc[permission.module]) {
				acc[permission.module] = [];
			}
			acc[permission.module].push(permission);
			return acc;
		},
		{} as Record<string, PermissionMetadata[]>
	);
}

/**
 * Format a permission for display (fallback for unknown permissions)
 */
export function formatPermissionDisplay(key: string): { resource: string; action: string } {
	const metadata = getPermissionMetadata(key);
	if (metadata) {
		return { resource: metadata.resource, action: metadata.action };
	}

	// Fallback: parse the permission key
	const parts = key.split("_");
	if (parts.length >= 3) {
		// Pattern: Module_Resource_Action or Module_Resource_Action_MoreAction
		const resource = parts[1].replace(/([A-Z])/g, " $1").trim();
		const action = parts.slice(2).join(" ").replace(/([A-Z])/g, " $1").trim();
		return { resource, action };
	}

	return { resource: key, action: "" };
}
